import { apiFetch } from './client';
import type {
  AvailableDeployment,
  CreateDeploymentResponseV1,
  Deployment,
  DeploymentAccessScope,
  DeploymentSelectionPolicy,
  IssueAuthBootstrapResponseV1,
} from '@grid2/shared-types';

export type CustomerDeployment = Deployment;
export type EnrollmentResult = CreateDeploymentResponseV1;
export interface TunnelMutationResult { deployment_id: string; tunnel: { configured: boolean; operation: 'set' | 'rotate' | 'clear'; applied_version: number; health: string } }
export interface DecommissionDeploymentResult { version: 1; deployment: CustomerDeployment; changed: boolean; tunnel_clear: { status: 'cleared' | 'rejected' | 'unconfirmed' | 'not_connected' } }
export type AuthBootstrapLinkResult = IssueAuthBootstrapResponseV1;
export interface DeploymentOverview { deployments: CustomerDeployment[]; policy: DeploymentSelectionPolicy; latestVersion: string }

export const deploymentsApi = {
  overview(): Promise<DeploymentOverview> {
    return apiFetch('/api/deployments', { cache: 'no-store' });
  },
  async list(): Promise<CustomerDeployment[]> {
    return (await deploymentsApi.overview()).deployments;
  },
  available(): Promise<{ deployments: AvailableDeployment[]; policy: DeploymentSelectionPolicy }> {
    return apiFetch('/api/deployments/available', { cache: 'no-store' });
  },
  create(input: { region: string; name: string }): Promise<EnrollmentResult> {
    return apiFetch('/api/deployments', { method: 'POST', body: JSON.stringify(input) });
  },
  async rename(deploymentId: string, name: string): Promise<CustomerDeployment> {
    return (await apiFetch<{ deployment: CustomerDeployment }>(`/api/deployments/${encodeURIComponent(deploymentId)}`, {
      method: 'PATCH', body: JSON.stringify({ name }),
    })).deployment;
  },
  async updateAccess(deploymentId: string, accessScope: DeploymentAccessScope, teamIds: string[]): Promise<CustomerDeployment> {
    return (await apiFetch<{ deployment: CustomerDeployment }>(`/api/deployments/${encodeURIComponent(deploymentId)}/access`, {
      method: 'PUT', body: JSON.stringify({ accessScope, teamIds }),
    })).deployment;
  },
  async updatePolicy(policy: DeploymentSelectionPolicy): Promise<DeploymentSelectionPolicy> {
    return (await apiFetch<{ policy: DeploymentSelectionPolicy }>('/api/deployments/policy', {
      method: 'PUT',
      body: JSON.stringify({ defaultDeploymentId: policy.defaultDeploymentId ?? null, selectionLocked: policy.selectionLocked }),
    })).policy;
  },
  regenerate(deploymentId: string): Promise<EnrollmentResult> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/regenerate`, { method: 'POST' });
  },
  createAuthBootstrapLink(deploymentId: string, setupOrigin: string): Promise<AuthBootstrapLinkResult> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/auth-bootstrap`, {
      method: 'POST',
      body: JSON.stringify({ setup_origin: setupOrigin }),
    });
  },
  decommission(deploymentId: string): Promise<DecommissionDeploymentResult> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/decommission`, { method: 'POST' });
  },
  requestUpdate(deploymentId: string): Promise<{ deployment: CustomerDeployment; latestVersion: string }> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/update`, { method: 'POST' });
  },
  setTunnel(deploymentId: string, token: string, rotate = false): Promise<TunnelMutationResult> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/tunnel${rotate ? '/rotate' : ''}`, {
      method: rotate ? 'POST' : 'PUT', body: JSON.stringify({ token }),
    });
  },
  clearTunnel(deploymentId: string): Promise<TunnelMutationResult> {
    return apiFetch(`/api/deployments/${encodeURIComponent(deploymentId)}/tunnel`, { method: 'DELETE' });
  },
};
